import { pluralN, t } from "@/lib/i18n";
import { useCallback, useEffect, useRef, useState, type ComponentType } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BriefcaseBusiness, CalendarDays, ChartNoAxesCombined, CheckSquare2, ClipboardList, FileCheck2, KanbanSquare, LaptopMinimal, LayoutDashboard, LogOut, Menu, Moon, Search, Settings, Sun, UserRound, UsersRound, ArrowUpRight, Building2, ChevronRight, Command, PanelTop, Route } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Organization, User } from "@/types";
import { useRealtime } from "@/hooks/use-realtime";
import { hasPermission, type PermissionKey } from "@shared/permissions";
import { setTheme, storedTheme, watchSystemTheme, type ThemeMode } from "@/lib/theme";
import { CommandPalette } from "@/components/command-palette";

type NavItem = {
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  permission?: PermissionKey;
};
type NavGroup = {
  label: string;
  items: NavItem[];
};
type Notification = {
  id: number;
  isRead: boolean;
};

const initials = (value?: string | null) => {
  const text = (value ?? "").trim();
  if (!text) return "?";
  return text.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
};

/** A compact road mark shared by the workspace and sign-in screens. */
export function TruckMark() {
  return <span className="brand-mark" aria-hidden="true"><Route /></span>;
}

function SidebarNav({ groups, location, close }: { groups: NavGroup[]; location: string; close: () => void }) {
  return <nav className="nav-list" aria-label={t("spa_main_navigation")}>{groups.map((group) => <div key={group.label} className="contents">
    <span className="nav-label">{group.label}</span>
    {group.items.map(({ href, label, icon: Icon }) => {
      const active = location === href || location.startsWith(`${href}/`);
      return <Link key={href} href={href} onClick={close} className={cn("nav-item", active && "active")} aria-current={active ? "page" : undefined}><span className="nav-icon"><Icon className="ui-icon"/></span><span>{label}</span></Link>;
    })}
  </div>)}</nav>;
}

export function AppShell({ user, organization, children }: { user: User; organization: Organization | null; children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setThemeState] = useState<ThemeMode>(storedTheme);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const queryClient = useQueryClient();
  useRealtime();

  const notifications = useQuery<Notification[]>({ queryKey: ["notifications"], queryFn: () => api("/api/notifications"), staleTime: 30000 });
  const unread = notifications.data?.filter((item) => !item.isRead).length ?? 0;
  const allowed = (item: NavItem) => !item.permission || hasPermission(user.permissions, item.permission);

  const workspace: NavItem[] = [
    { label: t("nav_dashboard"), href: "/dashboard", icon: LayoutDashboard, permission: "view" },
    { label: t("nav_hiring"), href: "/hiring", icon: BriefcaseBusiness, permission: "view" },
    { label: t("nav_candidates"), href: "/candidates", icon: UsersRound, permission: "view" },
    { label: t("nav_hiring_pipeline"), href: "/kanban", icon: KanbanSquare, permission: "view" },
  ];
  const operations: NavItem[] = [
    { label: t("nav_interviews"), href: "/interviews", icon: CalendarDays, permission: "manage_interviews" },
    { label: t("nav_tasks"), href: "/tasks", icon: CheckSquare2, permission: "manage_tasks" },
    { label: t("nav_documents"), href: "/documents", icon: FileCheck2, permission: "upload_documents" },
    { label: t("nav_reports"), href: "/reports", icon: ChartNoAxesCombined, permission: "view_reports" },
  ];
  const management: NavItem[] = [
    { label: t("nav_employees"), href: "/employees", icon: UserRound, permission: "manage_users" },
    { label: t("nav_settings"), href: "/settings", icon: Settings, permission: "manage_settings" },
    { label: t("nav_audit_log"), href: "/audit", icon: ClipboardList },
  ];
  const roleLabels: Record<string, string> = {
    system_admin: t("role_admin"),
    admin: t("role_admin"),
    company_admin: t("role_admin"),
    recruiter: t("role_recruiter"),
    document_specialist: t("role_document_specialist"),
    observer: t("role_observer"),
  };

  const groups: NavGroup[] = [
    { label: t("nav_workspace"), items: workspace.filter(allowed) },
    { label: t("nav_operations"), items: operations.filter(allowed) },
    { label: t("nav_management"), items: management.filter(allowed) },
  ].filter((group) => group.items.length);

  useEffect(() => {
    document.body.classList.add("app-body");
    return () => document.body.classList.remove("app-body");
  }, []);

  // Lock page scroll while the mobile drawer covers it — otherwise the page
  // scrolls behind the overlay and iOS rubber-bands the content around.
  useEffect(() => {
    if (!mobileOpen) return;
    document.body.classList.add("nav-open");
    return () => document.body.classList.remove("nav-open");
  }, [mobileOpen]);

  // Keyboard users must not tab into the off-canvas drawer while it is closed.
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    sidebar.inert = !mobileOpen && window.matchMedia("(max-width: 780px)").matches;
    const query = window.matchMedia("(max-width: 780px)");
    const onChange = () => { sidebar.inert = !mobileOpen && query.matches; };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [mobileOpen]);

  useEffect(() => watchSystemTheme(() => setThemeState(storedTheme())), []);

  const chooseTheme = useCallback((mode: ThemeMode) => { setTheme(mode); setThemeState(mode); }, []);

  // The "/" badge in the search field used to be decoration — no key handler
  // existed anywhere in the app. Both shortcuts are real now.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target instanceof HTMLElement
        && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable);
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((value) => !value);
        return;
      }
      if (event.key === "/" && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        // On small phones the global search is hidden — fall back to the
        // palette instead of focusing nothing.
        if (searchRef.current && searchRef.current.offsetParent !== null) {
          searchRef.current.focus();
          searchRef.current.select();
        } else {
          setPaletteOpen(true);
        }
      }
      // The drawer covers the page on a phone and could only be dismissed by
      // hitting the backdrop, which is invisible to anyone using a keyboard.
      // Esc inside an open dialog or the palette belongs to that surface only.
      if (event.key === "Escape" && !event.defaultPrevented && mobileOpen && !document.querySelector("[role='dialog']")) {
        event.preventDefault();
        setMobileOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const closeMobileNav = useCallback((restoreFocus: boolean) => {
    setMobileOpen(false);
    if (restoreFocus) menuButtonRef.current?.focus();
  }, []);

  const logout = useMutation({
    mutationFn: () => api<void>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => { queryClient.clear(); window.location.replace("/login"); },
  });

  return <div className="app-shell">
    {/* The styles, the string and the `#main-content` target all shipped; the
        link itself never did, so keyboard users tabbed through the whole
        sidebar on every page. */}
    <a className="skip-link" href="#main-content">{t("skip_to_content")}</a>
    <aside ref={sidebarRef} className={cn("sidebar", mobileOpen && "open")} id="sidebar" aria-label={t("nav_workspace")}>
      <Link className="brand" href="/dashboard" onClick={() => closeMobileNav(false)}><TruckMark /><span><strong>DriverFlow</strong><small>{t("brand_subtitle")}</small></span></Link>
      <div className="workspace-identity"><span><Building2 /></span><div><small>YOUR WORKSPACE</small><strong>{organization?.name ?? "DriverFlow"}</strong></div></div>
      <SidebarNav groups={groups} location={location} close={() => closeMobileNav(false)}/>
      <div className="sidebar-foot">
        {hasPermission(user.permissions, "manage_vacancies") ? <Link className="sidebar-application" href="/hiring?tab=form" onClick={() => closeMobileNav(false)}><span><PanelTop /><ArrowUpRight /></span><strong>Make a great first impression.</strong><small>Shape your driver application</small></Link> : null}
        <button className="ghost-button full" type="button" disabled={logout.isPending} onClick={() => logout.mutate()}><LogOut className="ui-icon"/>{t("btn_sign_out")}</button>
        <div className="theme-toggle" role="group" aria-label={t("theme_appearance")}>
          {([["light", Sun, t("theme_light")], ["dark", Moon, t("theme_dark")], ["system", LaptopMinimal, t("theme_system")]] as const).map(([mode, Icon, label]) =>
            <button key={mode} type="button" className={theme === mode ? "active" : ""} aria-pressed={theme === mode} title={label} aria-label={label} onClick={() => chooseTheme(mode)}><Icon className="ui-icon"/></button>)}
        </div>
      </div>
    </aside>

    <div className="workspace">
      <header className="topbar">
        <button ref={menuButtonRef} className="mobile-menu" type="button" onClick={() => setMobileOpen(true)} aria-label={t("spa_open_navigation")} aria-controls="sidebar" aria-expanded={mobileOpen}><Menu className="ui-icon"/></button>
        <nav className="workspace-breadcrumb" aria-label="Breadcrumb"><span>{t("nav_workspace")}</span><ChevronRight /><strong>{[...workspace, ...operations, ...management].find((item) => location === item.href || location.startsWith(`${item.href}/`))?.label ?? (location === "/profile" ? t("profile_heading") : "DriverFlow")}</strong></nav>
        <div className="global-search-wrap">
          {/* The query moves into the list's own filter (and into the URL), so the
              launcher clears itself rather than showing a term that may no longer
              match what the list is filtered by. */}
          <form className="global-search" onSubmit={(event) => { event.preventDefault(); const form = event.currentTarget; const value = String(new FormData(form).get("q") ?? "").trim(); if (!value) return; navigate(`/candidates?q=${encodeURIComponent(value)}`); form.reset(); searchRef.current?.blur(); }}><Search className="ui-icon"/><input ref={searchRef} name="q" placeholder={t("search_placeholder")} aria-label={t("search_placeholder")} autoComplete="off"/><kbd>/</kbd></form>
        </div>
        <div className="topbar-actions">
          <button className="icon-button command-trigger" type="button" onClick={() => setPaletteOpen(true)} aria-label="Open command menu" title="Search and navigate · ⌘ K"><Command className="ui-icon"/></button>
          <Link className="icon-button notification-button" href="/notifications" aria-label={unread ? `${t("nav_notifications")}: ${pluralN(unread, ["unread notification", "unread notifications"])}` : t("nav_notifications")}><Bell className="ui-icon"/>{unread ? <span className="notification-count" aria-hidden="true">{unread > 99 ? "99+" : unread}</span> : null}</Link>
          <Link className="user-chip" href="/profile" aria-label={`${t("profile_heading")}: ${user.fullName}`}><span className="avatar" aria-hidden="true">{initials(user.fullName)}</span><span><strong>{user.fullName}</strong><small>{roleLabels[user.role] ?? user.position ?? user.role}</small></span></Link>
        </div>
      </header>
      <main className="content" id="main-content" tabIndex={-1}>{children}</main>
    </div>
    <button className={cn("sidebar-backdrop", mobileOpen && "open")} type="button" tabIndex={mobileOpen ? 0 : -1} aria-label={t("spa_close_navigation")} onClick={() => closeMobileNav(true)}/>
    <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} user={user}/>
  </div>;
}
